import { config } from '../config';
import { deriveMatchMinute } from '../domain/clock';
import { isInMatch } from '../domain/machine';
import type { MatchNoteRow, MatchRow, ObservationKind, ObservationRow } from '../types';
import { appendChunk, finalizeBlob, getBlob, listIncompleteBlobs } from '../data/blobStore';
import type { AudioBlobRow } from '../data/db';
import { TABLE, read, write } from '../data/db';
import { dispatch } from './bus';
import { errorMessage } from './errors';
import { newId } from './ids';
import { log } from './logger';
import { browserAudioPlatform } from './audioPlatform';
import type { AudioPlatform, MediaRecorderLike, MediaStreamLike } from './audioPlatform';
import { MicrophoneSession, canRecord } from './microphone';
import type { MicState } from './microphone';
import { browserProbeDeps, isPlayable, probePlayability } from './playability';
import type { PlayabilityProbe, ProbeDeps } from './playability';

/**
 * **The recorder** — design §8, build unit U-11. FLAG-1, FLAG-2, FLAG-4, AUD-1, AUD-2, AUD-3,
 * AUD-5, LIVE-4.
 *
 * §16's risk table rates this unit `L` for one reason: *"the least recoverable data in the
 * product."* A dropped tap costs one count that the user can re-tap. A recording that fails to
 * persist is gone, and the user finds out after the match when there is nothing to replay. Every
 * decision below is made against **"is it on disk"**, not against "did the API succeed".
 *
 * ── The four properties this file exists to hold ────────────────────────────
 *
 * 1. **The Observation is written at START** (§8.2, SES-10). Its `matchMinute` is derived from the
 *    state at the start tap and never recomputed, so a flag begun at 44′ and finalized by the Half
 *    tap is a 44′ flag. Written at *stop*, it would be filed at `halfLengthMin` — the most valuable
 *    flags in the match, filed at the wrong minute and sometimes earlier than they happened
 *    (§19 F-17).
 * 2. **Every chunk is on disk before the next one arrives** (§8.2 step 2). Each `dataavailable` is
 *    persisted in **its own** transaction on `audioBlobs` alone, as it arrives. What survives a hard
 *    kill is what was already written before the kill; §8.5's honest limit is that the final partial
 *    chunk does not survive, so worst-case loss is one `TIMESLICE_MS`.
 * 3. **`finalizeRecording()` is the one exit** (AUD-3, §8.5), idempotent under racing triggers, and
 *    every trigger routes through it: user stop · `VOICE_NOTE_MAX_S` · `MediaRecorder.onerror` ·
 *    track `ended` (a real phone call) · `pagehide` · `visibilitychange → hidden` · any `Match.state`
 *    transition. A fresh `MediaRecorder` per recording is what makes "wedged recorder" unreachable.
 * 4. **AUD-1's no-transcoding rule is structural.** Assembly is `new Blob(chunks, {type})` inside
 *    `blobStore.finalizeBlob` and nothing else. There is no re-muxing step here to be tempted into
 *    adding; if fragmented MP4 did not concatenate, the correct finding was that assumption A-2 is
 *    false, not that a fix-up belongs on this path.
 *
 * ── ENV-9 / NFR-1: nothing here may cost a tap ──────────────────────────────
 *
 * The recorder is never on the increment path. It is started by a Flag-bar press or a 450 ms hold,
 * both of which are already off the tap budget, and a microphone that fails takes FLAG-4's silent
 * path rather than blocking. `startFlag` is `async` but the surface does not await it before
 * accepting the next contact.
 *
 * ── AUD-5 ───────────────────────────────────────────────────────────────────
 *
 * *"No speech transcription and no LLM processing … audio playback is the v1 contract."* There is
 * nothing in this file that reads the audio. It is also not foreclosed: the blob is addressable by a
 * stable `audioBlobId` on an archive-resident row, so a later pipeline consumes it with no schema
 * migration.
 */

const MODULE = 'recorder';

/** AUD-3's exit reasons. Recorded on the outcome so the log says *which* trigger fired. */
export type FinalizeReason =
  | 'userStop'
  | 'voiceNoteMax'
  | 'recorderError'
  | 'trackEnded'
  | 'pageHide'
  | 'visibilityHidden'
  | 'stateTransition'
  | 'streamLost';

/**
 * **What a recording is *of*** — the three subjects, as a discriminated union.
 *
 * ── Why a union and not a nullable id ───────────────────────────────────────
 *
 * HT-8's voice (build unit U-13c) is a `MatchNote.audioBlobId`: an `AudioBlob` with **no**
 * Observation at all. The cheap way to express that is `observationId: string | null`, and it is the
 * wrong way — a nullable field lets every consumer keep reading `observationId` and forget the case,
 * so LIVE-4's tile border would happily key off a halftime note and the Flag bar would caption one
 * as a voice note against a hypothesis. A union makes the compiler enumerate the arms at each
 * consumer, so the day a fourth subject is added every surface is asked about it.
 *
 * The arms carry exactly what their requirement gives them and nothing else:
 *
 * | Arm | Requirement | Row it references |
 * |---|---|---|
 * | `flag` | FLAG-1 / FLAG-2 — *"no hypothesis"* | `Observation(kind='flag', hypothesisId=null)` |
 * | `voiceNote` | AUD-2 — *"attached to a hypothesis"* | `Observation(kind='voiceNote')` |
 * | `matchNote` | HT-8 — *"text, voice, or both"* | `MatchNote(phase='halftime')`, **no Observation** |
 *
 * `hypothesisId` lives on the `voiceNote` arm only, which is FLAG-2's *"a flag has no hypothesis"*
 * as a type rather than as a `null` a reader has to interpret.
 */
export type RecordingSubject =
  | { readonly kind: 'flag'; readonly observationId: string }
  | { readonly kind: 'voiceNote'; readonly observationId: string; readonly hypothesisId: string }
  | { readonly kind: 'matchNote'; readonly matchNoteId: string };

/** What the UI renders while a recording is live (FLAG-1's *"visible on the bar"*, LIVE-4). */
export interface RecordingHandle {
  /** What this recording is *of*. See {@link RecordingSubject} for why it is a union. */
  readonly subject: RecordingSubject;
  /** `null` on FLAG-4's silent path — the absence of an id *is* the silence. */
  readonly audioBlobId: string | null;
  readonly matchId: string;
  /** SES-10 — derived at the start tap, never at finalize. */
  readonly matchMinute: number;
  readonly startedAt: number;
  /** FLAG-4 — the flag was filed but nothing is being captured. */
  readonly silent: boolean;
}

/**
 * OBS-1's correlation id for one recording, whichever subject it has.
 *
 * Derived rather than stored, so there is no second field that could disagree with the union — and
 * named, so every log line in this file says *which row* a recording belongs to without each call
 * site re-switching on the arm.
 */
function subjectId(subject: RecordingSubject): string {
  return subject.kind === 'matchNote' ? subject.matchNoteId : subject.observationId;
}

export interface RecordingOutcome {
  readonly handle: RecordingHandle;
  readonly reason: FinalizeReason;
  readonly bytes: number;
  readonly mime: string;
  /**
   * §8.2's probe verdict, or `null` when there was nothing to probe (a silent flag) or no way to
   * ask (no `Audio` constructor). `null` is not "unplayable" — see `playability.ts`.
   */
  readonly playability: PlayabilityProbe | null;
}

/**
 * What one press or hold did — FLAG-1's *"single tap starts … single tap stops"*, as a value.
 *
 * A union rather than a nullable handle, because the three outcomes are genuinely different and a
 * surface that could not distinguish them would render the wrong thing for two of them. `refused` is
 * the domain saying no (before kickoff, §6.4a; a vanished match; a hypothesis that no longer
 * exists) — a normal outcome the surface reports, not an error.
 */
export type RecorderAction =
  | { readonly kind: 'started'; readonly handle: RecordingHandle }
  | { readonly kind: 'stopped'; readonly outcome: RecordingOutcome }
  | { readonly kind: 'refused' };

/** What a surface subscribes to. One value, because there is at most one recording at a time. */
export interface RecorderView {
  readonly recording: RecordingHandle | null;
  readonly mic: MicState;
  /** AUD-4's one-time re-enable hint, raised the first time an audio affordance is used without one. */
  readonly hint: string | null;
}

/** AUD-4's hint text, in one place so the bar and the Pre-Match card cannot say different things. */
export const RE_ENABLE_HINT =
  'Flags are being saved without audio. Turn the microphone on for Touchline in Settings to record them.';

// ─────────────────────────────────────────────────────────────────────────────
// One recording
// ─────────────────────────────────────────────────────────────────────────────

interface StartedRecording {
  readonly handle: RecordingHandle;
  finalize(reason: FinalizeReason): Promise<RecordingOutcome>;
}

/**
 * A single recording, from `MediaRecorder.start` to the one exit.
 *
 * Constructed fresh per recording and never re-used. There is no `reset`, no `pause`, and no way to
 * start this object twice — which is the *mechanism* behind AUD-3's *"the recorder is never left in
 * a state where a subsequent record attempt fails"*, rather than a claim about it.
 */
class ActiveRecording implements StartedRecording {
  private recorder: MediaRecorderLike | null = null;
  private finalizing: Promise<RecordingOutcome> | null = null;
  private teardowns: (() => void)[] = [];
  private stopArrived: (() => void) | null = null;
  private stopPromise: Promise<void> | null = null;
  /**
   * Serialises chunk writes into one tail promise.
   *
   * The writes are independent — each is its own transaction — but finalize has to know they have
   * all landed before it assembles, and a chain is the smallest thing that gives that without
   * holding one long transaction open across a 60-second recording.
   */
  private writeChain: Promise<void> = Promise.resolve();
  private mime = '';

  constructor(
    readonly handle: RecordingHandle,
    private readonly platform: AudioPlatform,
    private readonly probeDeps: ProbeDeps | null,
    /** `window` in a browser; `null` where there is no event target (Node). */
    private readonly lifecycle: EventTarget | null,
    /** Whether the page is currently hidden — see {@link RecorderDeps.isHidden}. */
    private readonly isHidden: () => boolean,
    /**
     * Called **synchronously**, once, the instant any trigger claims this recording.
     *
     * AUD-3: *"the recorder returns to idle; UI recording indicators clear."* Most of §8.5's triggers
     * fire *inside* this object — `onerror`, track `ended`, `pagehide`, `visibilitychange`, the
     * auto-stop — and the service, which is what the UI subscribes to, has no other way to learn
     * that they did. Without this the bar stays lit after a real interruption and `isRecording()`
     * lies until the user presses again: the indicator would be claiming a recording that had
     * already ended, which is the one thing a recording indicator must never do.
     *
     * Synchronous and before any await, so the indicator clears in the moment the trigger fires
     * rather than after the blob has been assembled and probed.
     */
    private readonly onExit: () => void,
  ) {}

  /**
   * OBS-1's context for every line this class writes.
   *
   * One getter rather than a field spread at eight call sites, and one *shape* rather than a bare
   * id: a log that says only `id` cannot be read without knowing which table to look in, and this
   * class now serves three subjects across two tables.
   */
  private get logContext(): { readonly subject: string; readonly subjectId: string } {
    return { subject: this.handle.subject.kind, subjectId: subjectId(this.handle.subject) };
  }

  /**
   * Builds the `MediaRecorder` and starts it with `MediaRecorder.start(TIMESLICE_MS)`.
   *
   * `requestedMime` is deliberately `null`: AUD-1 stores *what the recorder emits*, so asking for a
   * type would be choosing a container rather than recording one. U-1 found on the device that
   * `recorder.mimeType` can legally be `''` — so the type is read per chunk as well, and the first
   * non-empty answer wins.
   *
   * @throws {Error} when the recorder cannot be constructed or refuses to start. The caller has
   *   already committed the Observation, so this is not a lost flag: it becomes a recording with no
   *   chunks, which finalizes to a zero-byte blob and is labelled *audio unavailable* — D-19's
   *   "something happened at minute 34" survives.
   */
  start(stream: MediaStreamLike): void {
    const recorder = this.platform.createRecorder(stream, null);
    this.recorder = recorder;

    this.stopPromise = new Promise<void>((resolve) => {
      this.stopArrived = resolve;
    });

    recorder.ondataavailable = (event) => {
      this.handleChunk(event.data);
    };
    recorder.onstop = () => this.stopArrived?.();
    recorder.onerror = (event) => {
      log.error(MODULE, 'AUD-3: MediaRecorder.onerror — finalizing what is already on disk', {
        ...this.logContext,
        event: String(event),
      });
      void this.finalize('recorderError');
    };

    this.registerInterruptionTriggers(stream);
    recorder.start(config.TIMESLICE_MS);
    this.mime = recorder.mimeType;
    this.armAutoStop();

    log.info(MODULE, 'recording started', {
      ...this.logContext,
      matchMinute: this.handle.matchMinute,
      // U-1's finding, carried: `''` is a legal answer and must not be mistaken for a fault.
      mime: this.mime === '' ? null : this.mime,
      timesliceMs: config.TIMESLICE_MS,
    });
  }

  /**
   * **The only exit from the recording state** (AUD-3, design §8.5).
   *
   * Idempotent by construction: the first call captures the promise and every later call — a
   * `pagehide` racing the user's tap, a phone call arriving as the auto-stop fires — receives the
   * same one, with the same reason. Without that, two finalizes would run over one chunk list and
   * the second would find the row already `complete`.
   *
   * **Never rejects.** `stop()` may or may not produce a final `dataavailable`; either way the
   * chunks already persisted *are* the audio, so a missing stop event costs at most the trailing
   * partial chunk and must not cost the segment. A recording that threw on the way out would be a
   * recorder left un-idle, which is the exact state AUD-3 forbids.
   */
  finalize(reason: FinalizeReason): Promise<RecordingOutcome> {
    if (this.finalizing) return this.finalizing;
    // Before anything async, so the UI stops claiming to record in the moment the trigger fires.
    // Inside the idempotence guard, so a racing second trigger cannot run it twice.
    this.onExit();
    this.finalizing = this.runFinalize(reason);
    return this.finalizing;
  }

  private async runFinalize(reason: FinalizeReason): Promise<RecordingOutcome> {
    const recorder = this.recorder;

    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch (cause) {
        log.warn(MODULE, 'MediaRecorder.stop() threw; the persisted chunks are still the audio', {
          ...this.logContext,
          error: errorMessage(cause),
        });
      }
      // A stop event that never comes must not strand the segment (see RECORDER_STOP_TIMEOUT_MS).
      await Promise.race([this.stopPromise ?? Promise.resolve(), delay(config.RECORDER_STOP_TIMEOUT_MS)]);
    }

    // Every chunk write already issued must land before the blob is assembled, or finalize would
    // concatenate a list that a straggler is still appending to.
    await this.writeChain.catch(() => undefined);
    this.teardown();

    const row = await this.assemble();
    const playability = await this.probe(row);

    log.info(MODULE, 'AUD-3: recording finalized', {
      ...this.logContext,
      reason,
      bytes: row?.bytes ?? 0,
      mime: row?.mime === '' ? null : (row?.mime ?? null),
      playable: playability === null ? null : isPlayable(playability),
    });

    return {
      handle: this.handle,
      reason,
      bytes: row?.bytes ?? 0,
      mime: row?.mime ?? '',
      playability,
    };
  }

  // ── internals ─────────────────────────────────────────────────────────────

  /**
   * §8.2 step 3 — assemble verbatim, mark complete.
   *
   * Wrapped rather than allowed to throw: the recording is already on disk as chunks, and a failed
   * *assembly* must not be reported to the user as a failed recording. §8.5's `pageshow` recovery
   * pass will find the row still `complete=false` and try again at the next launch, which is
   * strictly better than a thrown error that loses the reference to it.
   */
  private async assemble(): Promise<AudioBlobRow | null> {
    const id = this.handle.audioBlobId;
    if (id === null) return null;
    try {
      const result = await write(
        [TABLE.audioBlobs],
        () => finalizeBlob(id, this.mime),
        { label: 'finalizeAudio' },
      );
      return result.value;
    } catch (cause) {
      log.error(
        MODULE,
        'the recording could not be assembled; its chunks are still on disk and §8.5 recovery will retry at launch',
        { ...this.logContext, audioBlobId: id, error: errorMessage(cause) },
      );
      return null;
    }
  }

  /** §8.2's probe. A recording with no bytes is not probed — see `playability.isPlayable`. */
  private async probe(row: AudioBlobRow | null): Promise<PlayabilityProbe | null> {
    if (row?.blob == null || this.probeDeps === null) return null;
    return probePlayability(row.blob, row.mime, this.probeDeps);
  }

  /**
   * §8.2 step 2 — persist this chunk **now**, in its own transaction on `audioBlobs` only.
   *
   * The scope is declared at this call site rather than hidden inside `appendChunk`, which is why
   * `blobStore` guards with `requireTransaction`: a reader can see here that a chunk write touches
   * one table and joins nothing else.
   */
  private handleChunk(blob: Blob): void {
    const id = this.handle.audioBlobId;
    if (id === null) return;
    if (this.mime === '' && blob.type !== '') this.mime = blob.type;
    const mime = this.mime;

    this.writeChain = this.writeChain.then(async () => {
      try {
        await write([TABLE.audioBlobs], () => appendChunk(id, blob, mime), { label: 'audioChunk' });
      } catch (cause) {
        // Surfaced, never swallowed: one unwritable chunk is a hole in this recording, and losing
        // the rest of the recording to it would be strictly worse (NFR-8, AUD-3).
        log.error(MODULE, 'an audio chunk could not be persisted — this recording has a gap', {
          ...this.logContext,
          audioBlobId: id,
          bytes: blob.size,
          error: errorMessage(cause),
        });
      }
    });
  }

  /**
   * §8.5's trigger list, wired to the one exit.
   *
   * The user stop, `onerror` and the auto-stop are wired at their own sources; the state transition
   * is wired by {@link RecorderService.syncToMatch}. What is left is the pair that only a live
   * recording can register, plus the track `ended` that is a real phone call taking the microphone.
   *
   * `pagehide` **and** `visibilitychange` are both listened for, because they are not the same
   * event: WebKit can hide a page without a `visibilitychange` (§13.7 established this for the
   * writer lock and R-4 folded the same platform fact into §2.6's wake lock). Missing either one is
   * a recording that stops being written to with nothing on disk saying it ended.
   *
   * **Both are registered on the same target, and in a browser that target is `window`.** `pagehide`
   * is a page-transition event fired *at* `Window`, so a `document` listener is never on its
   * propagation path — `bootstrap.ts` shipped exactly that defect and it produced a listener that
   * never ran in any browser while every unit test passed. `visibilitychange` fires at `document`
   * but bubbles to `window`, so one `window` listener catches both; the pairing is asserted in the
   * browser pass rather than inferred here.
   */
  private registerInterruptionTriggers(stream: MediaStreamLike): void {
    for (const track of stream.getAudioTracks()) {
      const onEnded = () => {
        log.warn(MODULE, 'AUD-3: the audio track ended mid-recording — this is the phone-call path', {
          ...this.logContext,
        });
        void this.finalize('trackEnded');
      };
      track.addEventListener('ended', onEnded);
      this.teardowns.push(() => {
        track.removeEventListener('ended', onEnded);
      });
    }

    const target = this.lifecycle;
    if (target === null) return;

    const onPageHide = () => {
      void this.finalize('pageHide');
    };
    const onVisibility = () => {
      if (this.isHidden()) void this.finalize('visibilityHidden');
    };

    target.addEventListener('pagehide', onPageHide);
    target.addEventListener('visibilitychange', onVisibility);
    this.teardowns.push(() => {
      target.removeEventListener('pagehide', onPageHide);
    });
    this.teardowns.push(() => {
      target.removeEventListener('visibilitychange', onVisibility);
    });
  }

  /**
   * AUD-2 — *"auto-stop at `VOICE_NOTE_MAX_S` or on tap"*.
   *
   * One `setTimeout`, armed when a recording starts and cleared when it ends. NFR-5 permits exactly
   * this and §12.1a says why: *a cadence that runs whether or not there is work is banned; a
   * one-shot that exists only while there is work to do is not.* It applies to a flag as well as a
   * voice note — an unattended recorder is the one way this unit could quietly fill a phone.
   */
  private armAutoStop(): void {
    const handle = setTimeout(() => {
      log.info(MODULE, 'AUD-2: auto-stopping at VOICE_NOTE_MAX_S', {
        ...this.logContext,
        maxSeconds: config.VOICE_NOTE_MAX_S,
      });
      void this.finalize('voiceNoteMax');
    }, config.VOICE_NOTE_MAX_S * 1000);
    this.teardowns.push(() => {
      clearTimeout(handle);
    });
  }

  private teardown(): void {
    for (const undo of this.teardowns) {
      try {
        undo();
      } catch (cause) {
        log.warn(MODULE, 'a teardown step threw', { error: errorMessage(cause) });
      }
    }
    this.teardowns = [];
    if (this.recorder) {
      this.recorder.ondataavailable = null;
      this.recorder.onstop = null;
      this.recorder.onerror = null;
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─────────────────────────────────────────────────────────────────────────────
// The service
// ─────────────────────────────────────────────────────────────────────────────

/**
 * §8.2 step 1's shared prologue, as a value — everything decided **before** any row is written.
 *
 * The four fields are exactly the four §8.2 names in its first sentence, and they are computed once
 * per start regardless of subject. A subject that recomputed `matchMinute` at its own moment would
 * be re-introducing §19 F-17 (a 44′ flag filed at `halfLengthMin`) one entry point over.
 */
interface StartContext {
  /** The start moment. One reading, used for the minute, the row and the handle alike. */
  readonly at: number;
  /** `null` when the microphone is unavailable — the shared *fact* behind FLAG-4. */
  readonly stream: MediaStreamLike | null;
  /** Minted before a single byte exists (§8.2), `null` when there is nothing to record into. */
  readonly audioBlobId: string | null;
  /** SES-10, derived from the state at `at` and never recomputed. */
  readonly matchMinute: number;
}

/**
 * A subject's **commit** — the one step that genuinely differs between the three start paths.
 *
 * @returns the subject to publish, or `null` when the write was refused (a domain rejection, or a
 *   subject whose policy declines to file anything without a microphone).
 */
type CommitSubject = (ctx: StartContext) => Promise<RecordingSubject | null>;

export interface RecorderDeps {
  readonly platform?: AudioPlatform;
  readonly mic?: MicrophoneSession;
  /** `null` disables the probe — used where no `Audio` constructor exists. */
  readonly probeDeps?: ProbeDeps | null;
  /** Injected so a test can pin the moment a recording is stamped with. */
  readonly now?: () => number;
  /**
   * Where AUD-3's `pagehide` and `visibilitychange` triggers are registered. Defaults to `window`.
   *
   * A parameter for the reason `bootstrap.ts` gives for its own two targets: *"a bootstrap whose one
   * wiring obligation cannot be exercised by a test is a bootstrap whose omission the next unit
   * would rediscover in the field."* Node has no `window`, so without this the two triggers §8.5
   * names would be the only ones with no test at all — and they are the two that fire when the user
   * is about to lose the app.
   *
   * `null` runs with no lifecycle triggers, which is the honest state under Node, not a silent one.
   */
  readonly lifecycleTarget?: EventTarget | null;
  /**
   * Whether the page is hidden. Defaults to `document.visibilityState === 'hidden'`.
   *
   * Separate from the target for the same reason `bootstrap.ts` separates `isVisible`: the negative
   * — *a `visibilitychange` toward **visible** finalizes nothing* — is only assertable if a test can
   * present a page that is not hidden, which no `EventTarget` alone can do.
   */
  readonly isHidden?: () => boolean;
}

/** Hidden only when a real `document` says so. A missing document is not a hidden page. */
function documentIsHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/** `window` where there is one. Node has none, and pretending otherwise would hide the gap. */
function defaultLifecycleTarget(): EventTarget | null {
  return typeof window === 'undefined' ? null : window;
}

/**
 * The one recorder. Holds the microphone across the in-match states and at most one recording.
 *
 * "At most one" is a product decision the requirements imply and this class makes true: FLAG-1's
 * single tap stops the current recording, and a hold that began while one was running would
 * otherwise leave two recorders writing to two blobs with one indicator on screen.
 */
export class RecorderService {
  private readonly platform: AudioPlatform;
  private readonly mic: MicrophoneSession;
  private readonly probeDeps: ProbeDeps | null;
  private readonly now: () => number;
  private readonly lifecycleTarget: EventTarget | null;
  private readonly isHidden: () => boolean;
  private active: ActiveRecording | null = null;
  private listeners: ((view: RecorderView) => void)[] = [];
  private hint: string | null = null;
  /** The last `Match.state` {@link syncToMatch} was told about — see its note on why a remount is
   *  not a transition. `null` until the first capture surface mounts. */
  private lastState: MatchRow['state'] | null = null;

  constructor(deps: RecorderDeps = {}) {
    this.platform = deps.platform ?? browserAudioPlatform();
    this.mic = deps.mic ?? new MicrophoneSession(this.platform);
    this.probeDeps = deps.probeDeps === undefined ? browserProbeDeps() : deps.probeDeps;
    this.now = deps.now ?? (() => Date.now());
    this.lifecycleTarget =
      deps.lifecycleTarget === undefined ? defaultLifecycleTarget() : deps.lifecycleTarget;
    this.isHidden = deps.isHidden ?? documentIsHidden;
  }

  // ── What the UI reads ─────────────────────────────────────────────────────

  view(): RecorderView {
    return { recording: this.active?.handle ?? null, mic: this.mic.micState(), hint: this.hint };
  }

  /** @returns an unsubscribe function. */
  subscribe(listener: (view: RecorderView) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  micState(): MicState {
    return this.mic.micState();
  }

  dismissHint(): void {
    this.hint = null;
    this.emit();
  }

  // ── AUD-4, from the §6.0 Pre-Match card ───────────────────────────────────

  /** Must be called synchronously from the tap handler — iOS grants only inside a user gesture. */
  async requestMicrophone(): Promise<MicState> {
    const state = await this.mic.requestPermission();
    this.hint = null;
    this.emit();
    return state;
  }

  declineMicrophone(): void {
    this.mic.decline();
    this.emit();
  }

  // ── §8.1's lifecycle, bound to Match.state ────────────────────────────────

  /**
   * §8.1 + AUD-3 — the stream follows the state, and **any transition** finalizes a live recording.
   *
   * Called from the §5.6 chrome, which already re-renders on every `Match.state` change, so this is
   * the same binding §2.6 gives the wake lock: *"bound to `Match.state`, never to a mounted
   * screen."* Two obligations in one call because they share one trigger and separating them is how
   * one of them gets forgotten on a path the other is on.
   *
   * ── The two rules are different predicates, and conflating them was a defect ──
   *
   * | Obligation | Predicate |
   * |---|---|
   * | §8.1 — hold the stream | `isInMatch(state)` |
   * | §8.5 — finalize the recording | **the state changed at all** |
   *
   * The first version finalized only on the way *out* of the in-match states, which silently dropped
   * the case §8.2 uses as its own worked example: *"a flag started at 44:50 and stopped after the
   * Half tap."* `firstHalf → halftime` is a transition between two in-match states, so the stream
   * correctly stays held — and the recording must still end, because AUD-3 lists *"any `Match.state`
   * transition"* among its triggers. A browser pass found the bar still lit after the Half hold.
   *
   * ── Why the change is tracked rather than inferred from the argument ────────
   *
   * The chrome remounts whenever the user moves between capture surfaces (board ↔ Discovery), and
   * the effect runs again on every mount with the *same* state. Finalizing on every call would end a
   * recording the moment the user navigated — and §5.6 requires the opposite: *"an active recording
   * stays visible and stoppable"* across surfaces, which is the whole reason the recorder is a
   * service and not component state.
   *
   * The finalize runs **after** the transition has committed, which is §8.2's third consequence:
   * finalizing needs a non-IDB await, an IndexedDB transaction dies on one, and the Observation
   * already exists — so the state change commits, and this is the post-commit effect.
   */
  syncToMatch(state: MatchRow['state']): void {
    const changed = this.lastState !== null && this.lastState !== state;
    this.lastState = state;

    if (changed) void this.finalize('stateTransition');

    if (isInMatch(state)) {
      // Fire-and-forget: ENV-9/NFR-1 — acquiring the microphone must never delay a capture surface
      // rendering, and a failure here is FLAG-4's silent path, not an error the user must clear.
      void this.mic.acquire().catch((cause: unknown) => {
        log.warn(MODULE, '§8.1: the microphone could not be held for this match', {
          error: errorMessage(cause),
        });
        this.emit();
      });
      return;
    }
    this.mic.release();
  }

  /**
   * §8.1 — *"a `track.readyState` guard re-acquires the stream on `visibilitychange → visible` and
   * after an interruption, so a post-phone-call recording does not silently capture nothing."*
   *
   * ── Why this is eager and not left to the next recording ────────────────────
   *
   * `acquire()` runs the same guard, so a recording started after a phone call would replace the
   * ended stream on its own and would not capture silence. What that lazy path loses is the *reason*
   * §8.1 holds a stream at all: the first flag after the interruption would pay `getUserMedia`'s
   * 200–800 ms, and *"a per-recording acquisition loses the opening words of every flag, which is
   * the part that says what happened."* Re-acquiring at the resume moment restores the property
   * before the user needs it.
   *
   * ── This introduces no mechanism, which is the NFR-5 point ─────────────────
   *
   * `bootstrap.ts` already owns one `visibilitychange` / `pageshow` listener with two jobs (§13.7's
   * writer re-attempt and §2.6's wake lock); this is the third on the same one. No cadence, no
   * timer, and no request at all outside the in-match states — the same gate ENV-8 puts on the wake
   * lock, for the same reason: a request from a state that should hold nothing is the negative both
   * requirements forbid.
   *
   * A no-op when a healthy stream is already held, so a resume that changed nothing costs nothing.
   */
  refreshMicrophone(): void {
    if (this.lastState === null || !isInMatch(this.lastState)) return;
    if (this.mic.isHealthy()) return;
    void this.mic.acquire().catch((cause: unknown) => {
      log.warn(MODULE, '§8.1: the microphone could not be re-acquired after a resume', {
        error: errorMessage(cause),
      });
      this.emit();
    });
  }

  /** §8.1 / NFR-5 — drop the stream. Idempotent. */
  releaseMicrophone(): void {
    this.mic.release();
  }

  // ── Starting (§8.2 step 1) ────────────────────────────────────────────────

  /**
   * FLAG-1 / FLAG-2 — *"single tap starts recording immediately with no confirmation, stamping
   * match minute; single tap stops."*
   *
   * A press while a recording is live **stops it** and starts nothing, which is FLAG-1's second
   * sentence. The Flag bar therefore needs no mode of its own.
   */
  async pressFlag(match: MatchRow): Promise<RecorderAction> {
    return this.pressOrStart(match, (ctx) => this.commitObservation(match, ctx, 'flag', null));
  }

  /**
   * AUD-2 / LIVE-4 / DISC-3 — the 450 ms hold on a tile or a Discovery row.
   *
   * A hold while a recording is live finalizes it rather than starting a second one, so a hold that
   * lands during an already-running flag cannot produce two recorders and one indicator. Returning
   * `stopped` rather than silently doing nothing is what lets the surface show the same
   * acknowledgment for either outcome — a hold that "did nothing" is indistinguishable from a hold
   * the gesture layer never classified.
   */
  async holdVoiceNote(match: MatchRow, hypothesisId: string): Promise<RecorderAction> {
    return this.pressOrStart(match, (ctx) =>
      this.commitObservation(match, ctx, 'voiceNote', hypothesisId),
    );
  }

  /**
   * HT-8 — *"A halftime note (text, voice, or both) can be captured"* (build unit U-13c).
   *
   * ── The third subject, and the one that is not an Observation ───────────────
   *
   * §7.4 stores the halftime note as a `MatchNote(phase='halftime')`, so its audio hangs off
   * `MatchNote.audioBlobId` and **no Observation exists**. Reusing `pressFlag` would have been the
   * cheap route and would have been wrong twice over: every halftime note would arrive in FLAG-3's
   * unresolved-flag queue (DIST-1 derives that queue from `kind='flag'` Observations with no
   * resolution), and `PassTriage` would then refuse until the user dismissed their own notes.
   *
   * Everything *after* the commit is the same code the other two subjects run — the interruption
   * triggers, the auto-stop, the one exit, the playability probe — because it is literally the same
   * function (`pressOrStart`). Only the row that gets written differs.
   *
   * ── A press while recording stops, exactly as FLAG-1's does ─────────────────
   *
   * One control, both directions, so the note sheet needs no mode of its own.
   *
   * ── FLAG-4 does **not** transfer to this subject ────────────────────────────
   *
   * A flag with no microphone is still filed, because *"something happened at minute 34"* has value
   * on its own (D-19). A halftime note has no such residue: SES-10 fixes every halftime minute at
   * `halfLengthMin`, so a note with no text and no audio records nothing at all — and
   * `handleWriteMatchNote` already refuses that row as `ERR_NOTE_EMPTY`. So this path **writes
   * nothing** when the microphone is unavailable and returns `refused` with AUD-4's re-enable hint
   * raised, which the sheet surfaces. Text remains fully available, which is Andrew's ruling in both
   * directions: *"I want both text and voice available."*
   *
   * @param buildRow  the note as it should stand once recording begins, given the minted blob id and
   *   the start moment. A builder rather than a value because the id is minted by the shared
   *   prologue — the caller cannot know it before calling, and `app/recorder.ts` must not know how a
   *   halftime note is shaped.
   */
  async startMatchNoteAudio(
    match: MatchRow,
    buildRow: (audioBlobId: string, at: number) => MatchNoteRow,
  ): Promise<RecorderAction> {
    return this.pressOrStart(match, (ctx) => this.commitMatchNote(match, ctx, buildRow));
  }

  /**
   * FLAG-1's *"single tap starts … single tap stops"*, for **every** subject.
   *
   * A press while a recording is live finalizes it and starts nothing — so a hold that lands during
   * an already-running flag, or a record press in the note sheet while a flag is running, cannot
   * produce two recorders and one indicator. Returning `stopped` rather than silently doing nothing
   * is what lets a surface acknowledge either outcome: a press that "did nothing" is
   * indistinguishable from a press the gesture layer never classified.
   */
  private async pressOrStart(match: MatchRow, commit: CommitSubject): Promise<RecorderAction> {
    if (this.active) {
      const outcome = await this.finalize('userStop');
      return outcome === null ? { kind: 'refused' } : { kind: 'stopped', outcome };
    }
    const handle = await this.start(match, commit);
    return handle === null ? { kind: 'refused' } : { kind: 'started', handle };
  }

  /**
   * §8.2 step 1, in the order the design writes it — **the shared prologue and everything after it**.
   *
   * > *"mint `audioBlobId`; compute `matchMinute` from the state **now**; insert the `Observation` …
   * > in one transaction. Render the recording indicator on commit.
   * > `MediaRecorder.start(TIMESLICE_MS)`."*
   *
   * ── Why this is one function with a `commit` parameter ──────────────────────
   *
   * `new-path-parity-single-source-the-effect`. The existing path's full effect-set is: minute
   * derived at start · blob id minted before any bytes exist · **commit before
   * `MediaRecorder.start`** · FLAG-4 decision taken before anything is written · interruption
   * triggers registered · auto-stop armed · finalize plus playability probe. A third entry that
   * re-implemented any of those would be free to drop one — and the one it would drop is the
   * ordering, which is silent: nothing fails, chunks are simply refused and the recording has a hole.
   * So the third entry supplies **only** the commit, and inherits the other six by construction.
   *
   * **Commit before record, and that ordering is load-bearing.** Starting the recorder first would
   * let a `dataavailable` arrive against an `audioBlobId` whose row does not exist yet, and
   * `appendChunk` refuses those — a hole in the recording with the indicator already lit. The cost
   * is the commit interval, which A-1 measured at a p95 of 10 ms; §8.1's held stream is what keeps
   * FLAG-1's "immediately" true, not the ordering of these two lines.
   *
   * **The microphone verdict is taken here, before anything is written**, and each subject decides
   * what to do with it — see {@link commitObservation} for FLAG-4's silent flag and
   * {@link commitMatchNote} for why HT-8 refuses instead. The *fact* is shared; the *policy* is the
   * subject's, which is the one thing that genuinely differs between them.
   *
   * @returns the live handle, `null` when the commit was refused. Never throws for a microphone
   *   problem — on the Observation subjects that is a silent flag, not a failure.
   */
  private async start(match: MatchRow, commit: CommitSubject): Promise<RecordingHandle | null> {
    const at = this.now();
    const stream = await this.streamOrSilent();
    if (stream === null && this.mic.claimReEnableHint()) this.hint = RE_ENABLE_HINT;

    const ctx: StartContext = {
      at,
      stream,
      audioBlobId: stream === null ? null : newId(),
      // SES-10, at the start moment. Derived here and never again — §8.2's whole correction, and it
      // is in the prologue precisely so no subject can be added that derives it at its own moment.
      matchMinute: deriveMatchMinute(match, match.state, at),
    };

    const subject = await commit(ctx);
    if (subject === null) {
      this.emit();
      return null;
    }

    const handle: RecordingHandle = {
      subject,
      audioBlobId: ctx.audioBlobId,
      matchId: match.id,
      matchMinute: ctx.matchMinute,
      startedAt: at,
      silent: stream === null,
    };

    if (stream === null) {
      // FLAG-4 — filed, timestamped, and explicitly not recording. No `ActiveRecording` exists, so
      // the bar cannot render a recording state for it: the negative is structural. Only the
      // Observation subjects reach here; `commitMatchNote` refuses before this point.
      log.info(MODULE, 'FLAG-4: a silent flag was filed', {
        subject: subject.kind,
        subjectId: subjectId(subject),
        matchMinute: handle.matchMinute,
      });
      this.emit();
      return handle;
    }

    const recording: ActiveRecording = new ActiveRecording(
      handle,
      this.platform,
      this.probeDeps,
      this.lifecycleTarget,
      this.isHidden,
      // AUD-3 — whoever finalizes, the service stops holding it and every subscriber re-renders.
      // This is the ONLY place `active` is cleared, so there is one path off the recording state
      // rather than one per trigger.
      () => {
        if (this.active === recording) this.active = null;
        this.emit();
      },
    );
    try {
      recording.start(stream);
    } catch (cause) {
      // The row has committed, so the flag or note is real and correctly timed. What failed is the
      // capture — reported, with the blob row left `complete=false` for §8.5's launch recovery.
      log.error(MODULE, 'the recorder would not start; the row is filed with no audio', {
        subject: subject.kind,
        subjectId: subjectId(subject),
        error: errorMessage(cause),
      });
      this.emit();
      return handle;
    }

    this.active = recording;
    this.emit();
    return handle;
  }

  /**
   * FLAG-1 / FLAG-2 / AUD-2's commit — one `Observation`, plus its blob row, in one transaction.
   *
   * The FLAG-4 branch is here rather than in the prologue because it is *this* subject's policy:
   * *"the Observation is still filed — with `audioBlobId: null` — so 'something happened at minute
   * 34' survives (D-19). The bar does not move, does not disappear, and does not show a recording
   * indicator."*
   */
  private async commitObservation(
    match: MatchRow,
    ctx: StartContext,
    kind: Extract<ObservationKind, 'flag' | 'voiceNote'>,
    hypothesisId: string | null,
  ): Promise<RecordingSubject | null> {
    const observation: ObservationRow = {
      id: newId(),
      hypothesisId,
      matchId: match.id,
      matchMinute: ctx.matchMinute,
      kind,
      audioBlobId: ctx.audioBlobId,
      createdAt: ctx.at,
    };

    const result = await dispatch({ type: 'StartAudioObservation', matchId: match.id, observation });
    if (!result.ok) {
      log.warn(MODULE, 'the recording’s observation was refused, so nothing was started', {
        code: result.code,
        kind,
        matchId: match.id,
      });
      return null;
    }

    // FLAG-2 as a type: the `flag` arm has no `hypothesisId` to carry, so a flag can never be
    // published as something a tile could border (LIVE-4). A `voiceNote` with a null hypothesis is
    // not constructible either, which is why the fallback below is `flag` rather than a cast.
    return hypothesisId === null || kind === 'flag'
      ? { kind: 'flag', observationId: observation.id }
      : { kind: 'voiceNote', observationId: observation.id, hypothesisId };
  }

  /**
   * HT-8's commit — one `MatchNote`, plus its blob row, in one transaction.
   *
   * ── The refusal, and why it is not FLAG-4 ──────────────────────────────────
   *
   * With no microphone there is no `audioBlobId`, and a `MatchNote` with neither text nor audio is a
   * row that says nothing (`ERR_NOTE_EMPTY`). Writing one to preserve symmetry with FLAG-4 would
   * file a note the user would later open and find empty, with the app having claimed it saved
   * something. So nothing is written and the hint — already raised by the prologue — is what the
   * user sees.
   */
  private async commitMatchNote(
    match: MatchRow,
    ctx: StartContext,
    buildRow: (audioBlobId: string, at: number) => MatchNoteRow,
  ): Promise<RecordingSubject | null> {
    const audioBlobId = ctx.audioBlobId;
    if (audioBlobId === null) {
      log.info(MODULE, 'HT-8: no microphone, so no voice note was started and nothing was written', {
        matchId: match.id,
      });
      return null;
    }

    const row = buildRow(audioBlobId, ctx.at);
    const result = await dispatch({ type: 'StartMatchNoteAudio', matchId: match.id, row });
    if (!result.ok) {
      log.warn(MODULE, 'HT-8: the voice note’s row was refused, so nothing was started', {
        code: result.code,
        matchId: match.id,
      });
      return null;
    }
    return { kind: 'matchNote', matchNoteId: row.id };
  }

  /**
   * The stream to record on, or `null` for FLAG-4.
   *
   * The `readyState` guard lives in `MicrophoneSession.acquire`, so a stream ended by a phone call
   * between recordings is replaced rather than re-used — §8.1's *"a post-phone-call recording does
   * not silently capture nothing."*
   */
  private async streamOrSilent(): Promise<MediaStreamLike | null> {
    if (!canRecord(this.mic.micState()) && this.mic.micState() !== 'idle') return null;
    try {
      const acquired = await this.mic.acquire();
      return acquired.stream;
    } catch (cause) {
      log.warn(MODULE, 'FLAG-4: no microphone for this recording — filing it silent', {
        error: errorMessage(cause),
      });
      return null;
    }
  }

  // ── The one exit ──────────────────────────────────────────────────────────

  /**
   * AUD-3 — finalize whatever is running, whoever asked.
   *
   * `null` when nothing was running, which makes every caller safe to invoke unconditionally: a
   * `pagehide`, a state transition and the user's own tap all call this without first checking, and
   * a check each of them could forget is a recording each of them could strand.
   */
  async finalize(reason: FinalizeReason): Promise<RecordingOutcome | null> {
    const recording = this.active;
    if (!recording) return null;
    // No clearing here. `ActiveRecording.finalize` runs the `onExit` callback synchronously inside
    // its own idempotence guard, which is what clears `active` and emits — so an interruption that
    // never went through this method (a `pagehide`, a phone call, the auto-stop) reaches the UI by
    // the same path the user's own tap does. Clearing here as well would be a second exit, and
    // §8.5's whole contract is that there is one.
    const outcome = await recording.finalize(reason);
    this.emit();
    return outcome;
  }

  isRecording(): boolean {
    return this.active !== null;
  }

  // ── §8.5's launch recovery ────────────────────────────────────────────────

  /**
   * Design §8.5 — *"Recovery runs on `pageshow`/resume, not `pagehide`: any `AudioBlob` with
   * `complete=false` is assembled and probed at launch. Worst-case loss is one timeslice, not the
   * whole segment."*
   *
   * This is what makes the honest limit honest. A hard jetsam kill — the app swiped out of the
   * switcher — gives no `pagehide` and no `visibilitychange`, so nothing finalized; the chunks that
   * reached disk are still there under a row that says it was never assembled. Assembling them at
   * the next launch turns a dead row into a playable recording with its original minute intact.
   *
   * ── At launch, and deliberately **not** also on `pageshow` ──────────────────
   *
   * §8.5 names *"`pageshow`/resume"* and then *"at launch"*, and the two readings differ on exactly
   * one case: a **bfcache restore**, which fires `pageshow` on a document whose recorder is still
   * alive and still writing chunks. Recovering there would finalize the live recording's row out
   * from under it — `appendChunk` refuses a `complete` row by design, so every remaining chunk of
   * that recording would be dropped. A non-bfcache `pageshow` is a fresh load, which is the launch
   * this already covers, so the second trigger adds no recovery and one data-loss path.
   *
   * The active-recording skip below closes the hazard regardless of who calls this, because a guard
   * that depends on a call site is a guard the next call site does not have.
   *
   * Runs at launch, not on a cadence (NFR-5). Failures are logged per row and never abort the pass:
   * one unrecoverable recording must not cost the others.
   *
   * @returns how many rows were assembled.
   */
  async recoverIncompleteRecordings(): Promise<number> {
    const liveId = this.active?.handle.audioBlobId ?? null;
    const all = await read([TABLE.audioBlobs], () => listIncompleteBlobs(), 'listIncompleteBlobs');
    const rows = all.filter((row) => row.id !== liveId);
    if (rows.length === 0) return 0;

    log.warn(MODULE, '§8.5: found recordings a previous launch never finalized', {
      count: rows.length,
    });

    let recovered = 0;
    for (const row of rows) {
      try {
        await write([TABLE.audioBlobs], () => finalizeBlob(row.id, row.mime), {
          label: 'recoverAudio',
        });
        recovered++;
      } catch (cause) {
        log.error(MODULE, '§8.5: a recording could not be recovered and is left for the next launch', {
          audioBlobId: row.id,
          error: errorMessage(cause),
        });
      }
    }
    log.info(MODULE, '§8.5: recovery pass complete', { found: rows.length, recovered });
    return recovered;
  }

  private emit(): void {
    const view = this.view();
    for (const listener of [...this.listeners]) listener(view);
  }
}

/** The process-wide recorder. One microphone, one indicator, one exit. */
export const recorder = new RecorderService();

// ─────────────────────────────────────────────────────────────────────────────
// Reading a recording back
// ─────────────────────────────────────────────────────────────────────────────

/** §8.2's caption for one stored recording, for whichever surface is listing it. */
export interface AudioDescription {
  readonly audioBlobId: string | null;
  readonly bytes: number;
  readonly mime: string;
  /** `false` for a silent flag, a dangling id, or a row finalize never ran on. */
  readonly available: boolean;
  /** §8.2 — *"audio unavailable — flagged at 34′"*, or `null` when it is a normal note. */
  readonly caption: string | null;
}

/**
 * §8.2 — *"A dangling or incomplete blob is never presented as a normal note."*
 *
 * The three ways a recording is not a normal note, and each is a different sentence to the user:
 *
 * | Condition | Why it happens | Caption |
 * |---|---|---|
 * | no `audioBlobId` | FLAG-4 — the microphone was denied or absent | *silent flag* |
 * | id with no row | a dangling reference; OBS-2's orphan report exists for exactly this (§1.4) | *audio unavailable* |
 * | row not `complete` | a hard kill that §8.5's recovery has not yet closed | *audio unavailable* |
 *
 * Deriving this rather than storing it is deliberate (see `playability.ts`): a persisted verdict
 * would travel in a backup and mislabel a recording on the device it was restored to.
 *
 * @throws {StorageError} on a read failure — an empty list and a failed read look identical to the
 *   user and only one of them is worth retrying.
 */
export async function describeAudio(
  audioBlobId: string | null,
  matchMinute: number,
): Promise<AudioDescription> {
  if (audioBlobId === null) {
    return {
      audioBlobId: null,
      bytes: 0,
      mime: '',
      available: false,
      caption: `silent flag — flagged at ${String(matchMinute)}′`,
    };
  }

  const row = await read([TABLE.audioBlobs], () => getBlob(audioBlobId), 'describeAudio');
  if (row === undefined || !row.complete || row.blob === null || row.bytes === 0) {
    return {
      audioBlobId,
      bytes: row?.bytes ?? 0,
      mime: row?.mime ?? '',
      available: false,
      caption: `audio unavailable — flagged at ${String(matchMinute)}′`,
    };
  }
  return { audioBlobId, bytes: row.bytes, mime: row.mime, available: true, caption: null };
}
